import {
  PRODUCT_PHOTO_HEIGHT,
  PRODUCT_PHOTO_WIDTH
} from 'components/structure-page/2d/product-photo';
import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  CondenserPhotoData,
  FootprintPolygonsType,
  GridObjectType,
  GridSpaceDataType,
  LineSetType,
  ZoneOutlineData
} from 'data/types';
import {
  getLabelBounds,
  ZONE_LABEL_HEIGHT_NO_BTUS,
  ZONE_LABEL_HEIGHT_WITH_BTUS,
  ZONE_LABEL_WIDTH
} from 'utils/zone-outline';

export type BoundingBox = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

const OBJECT_RENDERED_TILES = 3;

export function computeAutoFitBoundingBox(
  gridSpaces: GridSpaceDataType[],
  objects: GridObjectType[],
  zoneOutlines: ZoneOutlineData[],
  condenserPhotos: CondenserPhotoData[] = []
): BoundingBox | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const space of gridSpaces) {
    minX = Math.min(minX, space.position.x);
    minY = Math.min(minY, space.position.y);
    maxX = Math.max(maxX, space.position.x + space.position.width);
    maxY = Math.max(maxY, space.position.y + space.position.height);
  }

  const objectSize = OBJECT_RENDERED_TILES * STRUCTURE_GRID_SIZE;
  for (const obj of objects) {
    const left = obj.position.x * STRUCTURE_GRID_SIZE;
    const top = obj.position.y * STRUCTURE_GRID_SIZE;
    minX = Math.min(minX, left);
    minY = Math.min(minY, top);
    maxX = Math.max(maxX, left + objectSize);
    maxY = Math.max(maxY, top + objectSize);
  }

  for (const outline of zoneOutlines) {
    if (outline.labelPosition) {
      const hasBTUs = outline.BTUs.heating > 0 || outline.BTUs.cooling > 0;
      const labelHeight = hasBTUs
        ? ZONE_LABEL_HEIGHT_WITH_BTUS
        : ZONE_LABEL_HEIGHT_NO_BTUS;
      const bounds = getLabelBounds(
        outline.labelPosition.x,
        outline.labelPosition.y,
        ZONE_LABEL_WIDTH,
        labelHeight,
        outline.labelPosition.anchor,
        outline.labelPosition.placement
      );
      minX = Math.min(minX, bounds.left);
      minY = Math.min(minY, bounds.top);
      maxX = Math.max(maxX, bounds.right);
      maxY = Math.max(maxY, bounds.bottom);
    }
  }

  for (const photo of condenserPhotos) {
    const left = photo.position.x - PRODUCT_PHOTO_WIDTH / 2;
    const top = photo.position.y - PRODUCT_PHOTO_HEIGHT / 2;
    minX = Math.min(minX, left);
    minY = Math.min(minY, top);
    maxX = Math.max(maxX, left + PRODUCT_PHOTO_WIDTH);
    maxY = Math.max(maxY, top + PRODUCT_PHOTO_HEIGHT);
  }

  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

export function computeFootprintBoundingBox(
  footprintPolygons: FootprintPolygonsType | null
): BoundingBox | null {
  const points = (footprintPolygons ?? [])
    .filter((polygon) => polygon.length >= 3)
    .flat();
  if (points.length === 0) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const point of points) {
    const px = point.x * STRUCTURE_GRID_SIZE;
    const py = point.y * STRUCTURE_GRID_SIZE;
    minX = Math.min(minX, px);
    minY = Math.min(minY, py);
    maxX = Math.max(maxX, px);
    maxY = Math.max(maxY, py);
  }

  return { minX, minY, maxX, maxY };
}

export function computeLinesetBoundingBox(
  lineSets: LineSetType[],
  levelUniqueId?: string
): BoundingBox | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const lineSet of lineSets) {
    for (const point of lineSet.points) {
      const onOtherLevel =
        !!levelUniqueId &&
        !!point.levelUniqueId &&
        point.levelUniqueId !== levelUniqueId;
      if (!onOtherLevel) {
        const px = point.positionX * STRUCTURE_GRID_SIZE;
        const py = point.positionY * STRUCTURE_GRID_SIZE;
        minX = Math.min(minX, px);
        minY = Math.min(minY, py);
        maxX = Math.max(maxX, px);
        maxY = Math.max(maxY, py);
      }
    }
  }

  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

export function unionBoundingBoxes(
  boxes: (BoundingBox | null)[]
): BoundingBox | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const box of boxes) {
    if (box) {
      minX = Math.min(minX, box.minX);
      minY = Math.min(minY, box.minY);
      maxX = Math.max(maxX, box.maxX);
      maxY = Math.max(maxY, box.maxY);
    }
  }

  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

export function computeLargestLevelAutoFitBox(
  levelBoxes: (BoundingBox | null)[],
  centerBoxes: (BoundingBox | null)[] = []
): BoundingBox | null {
  let fitHalfWidth = 0;
  let fitHalfHeight = 0;
  const layoutBoxes: BoundingBox[] = [];

  for (let index = 0; index < levelBoxes.length; index += 1) {
    const content = levelBoxes[index];
    if (content) {
      const centerBox = centerBoxes[index] ?? content;
      layoutBoxes.push(centerBox);
    }
  }

  const centerSource = unionBoundingBoxes(layoutBoxes);
  if (!centerSource) return null;

  const centerX = (centerSource.minX + centerSource.maxX) / 2;
  const centerY = (centerSource.minY + centerSource.maxY) / 2;

  for (let index = 0; index < levelBoxes.length; index += 1) {
    const content = levelBoxes[index];
    if (content) {
      fitHalfWidth = Math.max(
        fitHalfWidth,
        centerX - content.minX,
        content.maxX - centerX
      );
      fitHalfHeight = Math.max(
        fitHalfHeight,
        centerY - content.minY,
        content.maxY - centerY
      );
    }
  }
  return {
    minX: centerX - fitHalfWidth,
    minY: centerY - fitHalfHeight,
    maxX: centerX + fitHalfWidth,
    maxY: centerY + fitHalfHeight
  };
}
